import { useEffect, useRef, useState } from 'react'
import { Note, ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { SurveyMediaDto, SurveyMediaPlaybackDto } from '@/shared/api/types'
import { getPlayback, listMedia } from './api'
import { Pill, duration } from './vocab'

/**
 * The recordings attached to one interview, and the permission that gates them.
 *
 * ## Two clips, not one
 *
 * `AUDIO_CONSENT` and `AUDIO_INTERVIEW` are separate objects on purpose. A
 * supervisor checking that consent was actually taken should not have to listen
 * to the respondent's answers to do it, and a screen that concatenated them would
 * make that impossible. So each clip is listed and played on its own.
 *
 * ## Playing is a different disclosure from reading
 *
 * `survey:listen` is a separate authority from `survey:read`. Reading what
 * somebody said and hearing them say it are not the same act — a voice carries
 * identity that a transcript does not — and every play is written to the audit
 * log by name.
 *
 * The Play control is therefore **offered to everybody** and the 403
 * (`survey/listen-denied`) is rendered as the answer. Hiding it would be
 * indistinguishable from the feature being broken, and the server's refusal is a
 * paragraph explaining the distinction, which is far more use than a greyed-out
 * button. That is rule 3, and this is the screen it was written for.
 *
 * ## The URL is short-lived by design
 *
 * `getPlayback` mints a presigned GET that expires in minutes; the bytes never
 * transit the application. An expired link is the normal end of its life, not an
 * error — so when it lapses the player is replaced by a re-fetch, and a mid-play
 * failure re-fetches once before saying anything, because the overwhelmingly
 * likely cause is that the clock ran out while the panel sat open.
 */
export function AudioPlayer({ responseId }: { responseId: string }) {
  const list = useLoad(() => listMedia(responseId), [responseId])

  const [active, setActive] =
    useState<{ mediaId: string; playback: SurveyMediaPlaybackDto } | null>(null)
  const [expired, setExpired] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // One silent retry per clip. Without the guard a permanently dead URL and an
  // expired one look the same to `onError` and the component re-fetches forever.
  const retried = useRef<string | null>(null)

  // The link dies on a wall clock the server set, so the player has to as well.
  // Leaving a lapsed <audio> on screen is the "broken player" this is here to
  // avoid: it fails silently on the next seek with nothing to explain it.
  useEffect(() => {
    if (!active) return
    const ms = Date.parse(active.playback.expiresAt) - Date.now()
    if (!Number.isFinite(ms) || ms <= 0) { setExpired(true); return }
    const handle = setTimeout(() => setExpired(true), ms)
    return () => clearTimeout(handle)
  }, [active])

  useEffect(() => { setActive(null); setExpired(false); retried.current = null }, [responseId])

  async function play(mediaId: string) {
    setBusy(mediaId)
    setError(null)
    try {
      const playback = await getPlayback(mediaId)
      setActive({ mediaId, playback })
      setExpired(false)
    } catch (e) {
      // Includes `survey/listen-denied` (403) and "reserved but never uploaded"
      // (409). Both arrive as server-composed sentences; neither is paraphrased
      // here, because the server knows which of them it was and this does not.
      setActive(null)
      setError(explain(e))
    } finally {
      setBusy(null)
    }
  }

  const data = list.data
  const clips = (data?.media ?? []).filter((m) => m.kindCode.startsWith('AUDIO_'))
  const others = (data?.media ?? []).filter((m) => !m.kindCode.startsWith('AUDIO_'))

  return (
    <div className="space-y-3">
      <ErrorStrip message={list.error} />
      {error && <Note tone="bad">{error}</Note>}

      {list.loading && !data ? <LoadingBlock label="Looking for recordings…" /> : data && (
        <>
          {clips.length === 0 ? (
            <Note tone="info">
              {data.audioRecordingMode === 'OFF'
                ? 'This poll does not record audio, so there is nothing to play here and the '
                  + 'AUDIO_MISSING rule will never fire on it.'
                : `Audio is ${data.audioRecordingMode === 'REQUIRED' ? 'required' : 'optional'} `
                  + 'on this poll and no recording reached storage for this interview. That '
                  + 'absence is itself what the AUDIO_MISSING rule looks for — check the flags '
                  + 'above before assuming it is a sync problem.'}
            </Note>
          ) : clips.map((clip) => (
            <ClipRow
              key={clip.id}
              clip={clip}
              busy={busy === clip.id}
              playback={active?.mediaId === clip.id ? active.playback : null}
              expired={active?.mediaId === clip.id && expired}
              onPlay={() => { retried.current = null; void play(clip.id) }}
              onLoaded={() => { retried.current = null }}
              onFailed={() => {
                if (retried.current === clip.id) { setExpired(true); return }
                retried.current = clip.id
                void play(clip.id)
              }}
            />
          ))}

          {others.length > 0 && (
            <p className="text-erp-xs text-muted-foreground">
              {others.length} other attachment{others.length === 1 ? '' : 's'} on this interview
              ({others.map((m) => m.kindCode).join(', ')}). Not audio, so there is no player for
              {others.length === 1 ? ' it' : ' them'} here.
            </p>
          )}

          <p className="text-erp-xs text-muted-foreground">
            Playing a clip needs <span className="font-mono">survey:listen</span>, which is a
            separate authority from <span className="font-mono">survey:read</span> — and
            <strong className="font-medium"> every play is written to the audit log with your
            name on it</strong>. The link the player uses is short-lived and issued per play;
            the audio itself never passes through this application.
          </p>
        </>
      )}
    </div>
  )
}

const KIND_MEANING: Record<string, string> = {
  AUDIO_INTERVIEW: 'The interview itself.',
  AUDIO_CONSENT: 'The consent exchange only — recorded separately so consent can be verified '
    + 'without listening to what the respondent answered.',
}

function ClipRow({ clip, playback, expired, busy, onPlay, onLoaded, onFailed }: {
  clip: SurveyMediaDto
  playback: SurveyMediaPlaybackDto | null
  expired: boolean
  busy: boolean
  onPlay: () => void
  onLoaded: () => void
  onFailed: () => void
}) {
  return (
    <div className="erp-card p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Pill>{clip.kindCode}</Pill>
            <span className="text-erp-sm tabular-nums">{duration(clip.durationSeconds)}</span>
            <Pill>{clip.statusCode}</Pill>
            {clip.transcriptStateCode !== 'NONE' && (
              <Pill>{`transcript ${clip.transcriptStateCode.toLowerCase()}`}</Pill>
            )}
          </div>
          <p className="text-erp-xs text-muted-foreground mt-1">
            {KIND_MEANING[clip.kindCode] ?? 'An audio attachment on this interview.'}
            {clip.capturedAt && ` Captured ${fmtDateTime(clip.capturedAt)}.`}
          </p>
        </div>
        {/*
          Never disabled on the authority. A person without `survey:listen` gets
          the server's explanation of why listening is granted separately, which
          is the thing they actually need in order to go and ask for it.
        */}
        <button
          type="button"
          className="erp-btn h-8 text-erp-xs shrink-0"
          disabled={busy}
          onClick={onPlay}
        >
          {busy ? 'Fetching link…' : playback && !expired ? 'Refresh link' : 'Play'}
        </button>
      </div>

      {playback && !expired && (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            controls
            className="w-full"
            src={playback.url}
            onLoadedMetadata={onLoaded}
            onError={onFailed}
          />
          <p className="text-erp-xs text-muted-foreground tabular-nums">
            Link expires {fmtDateTime(playback.expiresAt)}. This play is now in the audit log.
          </p>
        </>
      )}

      {playback && expired && (
        <Note tone="info">
          The playback link has expired — that is how it is meant to end, not a fault. Press
          Play to mint a fresh one; it is a new audit entry, because it is a new play.
        </Note>
      )}
    </div>
  )
}
